import { isCrossOrganizationTarget, rolloutApiClient, staffRolloutPath, type RolloutTarget } from './rollout_target'

/**
 * Admin console UI variant rollout (PLAN-20260909). Organisation-scoped
 * policy served by governance-service; the browser renders, the server decides.
 * A target other than the active organisation goes through the staff routes
 * (PLAN-20260930 slice 5); the active organisation uses its own routes.
 */
export type UIVariantRolloutLevel = 'off' | 'default-next' | 'next-only'

export interface UIVariantAdoption {
  next: number
  legacy: number
}

export interface UIVariantRolloutPolicy {
  organizationId: string
  level: UIVariantRolloutLevel
  revision: number
  environmentLocked: boolean
  levels: UIVariantRolloutLevel[]
  adoption: UIVariantAdoption
}

export interface UIVariantRolloutHistoryEntry {
  organizationId: string
  revision: number
  previousLevel: UIVariantRolloutLevel
  level: UIVariantRolloutLevel
  actorId: string
  reason: string
  changedAt: string
}

interface UIVariantRolloutHistoryResponse {
  history: UIVariantRolloutHistoryEntry[]
}

export interface ChangeUIVariantRolloutPolicy {
  expectedRevision: number
  level: UIVariantRolloutLevel
  reason: string
  confirmed: true
}

function policyPath(target?: RolloutTarget): string {
  return isCrossOrganizationTarget(target) ? staffRolloutPath('ui-variant', target.organizationId) : 'ui-variant-rollout'
}

function historyPath(target?: RolloutTarget): string {
  return isCrossOrganizationTarget(target)
    ? staffRolloutPath('ui-variant', target.organizationId, 'history')
    : 'ui-variant-rollout/history'
}

export function getUIVariantRolloutPolicy(target?: RolloutTarget): Promise<UIVariantRolloutPolicy> {
  return rolloutApiClient(target).get<UIVariantRolloutPolicy>(policyPath(target))
}

export async function listUIVariantRolloutHistory(target?: RolloutTarget): Promise<UIVariantRolloutHistoryEntry[]> {
  const response = await rolloutApiClient(target).get<UIVariantRolloutHistoryResponse>(historyPath(target))
  return response.history ?? []
}

export function changeUIVariantRolloutPolicy(
  command: ChangeUIVariantRolloutPolicy,
  target?: RolloutTarget,
): Promise<UIVariantRolloutPolicy> {
  return rolloutApiClient(target).put<UIVariantRolloutPolicy, ChangeUIVariantRolloutPolicy>(policyPath(target), command)
}
